import React from 'react';
import { Spinner } from '@amzn/awsui-components-react';
import { SegmentExpansionStatus } from 'src/components/reports/lefReport/useSegmentCliExpansion';
import { MAX_SEGMENT_PAGES, SEGMENT_FETCH_PAGE_SIZE } from 'src/components/reports/cliProvenance';
import 'src/components/reports/lefReport/CliProvenanceHeaders.scss';

const pluralCli = (count: number) => `${count} CLI${count === 1 ? '' : 's'}`;

/** Caption under a segment-total header: what a click will do, or what is happening. */
export function segmentTotalCaption(status: SegmentExpansionStatus): string {
    if (status.loading) {
        return 'Loading CLIs…';
    }
    if (status.error) {
        return 'Could not load CLIs. Select to retry.';
    }
    if (status.expanded && status.cliCount !== undefined) {
        const shown = `${pluralCli(status.cliCount)} shown`;
        return status.truncated ? `${shown} (first ${MAX_SEGMENT_PAGES * (SEGMENT_FETCH_PAGE_SIZE - 1)} only)` : shown;
    }
    if (status.cliCount !== undefined) {
        return pluralCli(status.cliCount);
    }
    return 'Show CLIs';
}

/**
 * "Total <functional segment>" column header (Story 3.1). A real button, so it is keyboard- and
 * screen-reader-operable; the dotted underline is the drillable affordance that tells it apart from
 * the non-drillable reconciliation headers.
 */
export function SegmentTotalHeader(props: {
    label: string;
    segmentName: string;
    status: SegmentExpansionStatus;
    onToggle: () => void;
}) {
    const { label, segmentName, status, onToggle } = props;
    const action = status.expanded ? 'Hide' : 'Show';
    return (
        <div className="lef-cli-provenance__segment-header">
            <button
                type="button"
                className="lef-cli-provenance__segment-toggle"
                aria-expanded={status.expanded}
                aria-label={`${action} CLIs in ${segmentName}`}
                title={`${action} the CLIs in ${segmentName}`}
                onClick={onToggle}>
                {label}
            </button>
            <span
                className={`lef-cli-provenance__caption${status.error ? ' lef-cli-provenance__caption--error' : ''}`}
                role={status.error ? 'alert' : undefined}>
                {status.loading && <Spinner size="normal" />} {segmentTotalCaption(status)}
            </span>
            {status.error && <span className="lef-cli-provenance__visually-hidden">{status.error}</span>}
        </div>
    );
}

/**
 * Header of a revealed CLI column: the CLI (with currency when shown) over its segment → counterparty
 * caption from the segment response's DETAIL rows.
 */
export function CliColumnHeader(props: { label: string; caption?: string }) {
    const { label, caption } = props;
    return (
        <div className="lef-cli-provenance__cli-header">
            <span>{label}</span>
            {caption && <span className="lef-cli-provenance__caption">{caption}</span>}
        </div>
    );
}
